import {
  ProviderProps,
  ReactElement,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';

import { Nullable } from '~globals/types/commons';
import { selectAuth } from '~store/slices/auth';
import { ApiTransactionRoutes } from '~services/apiTransaction/types';
import {
  useAppSelector,
  useGenerateRoutesSelectedData,
  useGenerateRoutesChargeIndicatorsData,
} from '~hooks/index';

import { AssistantGenerateRoutesDialogTab } from '../types';
import { TABLE_KEY_FIELDS } from '../utils/constants';
import { getDisabledVehicle, getDisabledItem } from '../utils/helpers';

import {
  AssistantGenerateRoutesDialogByItemsContextValue,
  AssistantGenerateRoutesDialogByItemsProviderProps,
} from './types';

const AssistantGenerateRoutesDialogByItemsContext =
  createContext<AssistantGenerateRoutesDialogByItemsContextValue>({
    onClose: () => {},
    activeTab: { tab: 'form', setActiveTab: () => {} },
    data: {
      items: [],
      warehouses: { list: [], lastSelectedWarehouse: null },
      vehicles: { value: [], setValue: () => {} },
      selectedDriver: {
        value: null,
        setValue: () => {},
      },
      isFeedbackRequestAudit: false,
    },
    form: {
      selection: {
        orders: {
          handleOnSelect: () => {},
          handleOnSelectAll: () => {},
          restarSelectedRows: () => {},
          selectedRows: [],
          selectedRowsIds: [],
          selectRowsProps: {
            mode: 'checkbox',
            clickToSelect: false,
            selected: undefined,
            onSelectAll: () => {},
            onSelect: () => {},
            selectionHeaderRenderer: undefined,
            selectionRenderer: undefined,
            nonSelectable: undefined,
            nonSelectableClasses: undefined,
          },
          setSelectedRowsIds: () => {},
          setSelectedRows: () => {},
        },
        vehicles: {
          handleOnSelect: () => {},
          handleOnSelectAll: () => {},
          restarSelectedRows: () => {},
          selectedRows: [],
          selectedRowsIds: [],
          selectRowsProps: {
            mode: 'checkbox',
            clickToSelect: false,
            selected: undefined,
            onSelectAll: () => {},
            onSelect: () => {},
            selectionHeaderRenderer: undefined,
            selectionRenderer: undefined,
            nonSelectable: undefined,
            nonSelectableClasses: undefined,
          },
          setSelectedRowsIds: () => {},
          setSelectedRows: () => {},
        },
      },
      chargeIndicators: {
        orders: { capacity1: 0, capacity2: 0, numberOfPackages: 0 },
        vehicles: { capacity1: 0, capacity2: 0, maximumVisits: 0 },
      },
      isScheduleRouteEnabled: {
        value: false,
        setValue: () => {},
      },
    },
    stepper: {
      currentApiTransactionId: {
        value: null,
        setCurrentApiTransactionId: () => {},
      },
      currentRoutes: {
        value: [],
        setCurrentRoutes: () => {},
      },
      isSuccesCompleted: {
        value: false,
        setIsSuccesCompleted: () => {},
      },
    },
    actions: undefined,
  });

export const useAssistantGenerateRoutesDialogByItemsContext =
  (): AssistantGenerateRoutesDialogByItemsContextValue =>
    useContext(AssistantGenerateRoutesDialogByItemsContext);

const AssistantGenerateRoutesDialogByItemsProvider = ({
  value,
  children,
}: ProviderProps<AssistantGenerateRoutesDialogByItemsProviderProps>): ReactElement => {
  const { user } = useAppSelector(selectAuth);

  const [activeTab, setActiveTab] =
    useState<AssistantGenerateRoutesDialogTab>('form');

  const [isScheduleRouteEnabled, setIsScheduleRouteEnabled] =
    useState<boolean>(false);

  const selection = useGenerateRoutesSelectedData({
    vehiclesData: value.data.vehicles.value,
    ordersData: value.data.items,
    keysFields: TABLE_KEY_FIELDS,
    getDisabledVehicle,
    getDisabledOrder: (currentOrder) => getDisabledItem(currentOrder, user),
    isScheduleRouteEnabled,
  });

  const chargeIndicators = useGenerateRoutesChargeIndicatorsData({
    selectedVehicles: selection.vehicles.selectedRows,
    selectedOrders: selection.orders.selectedRows,
  });

  const [currentApiTransactionId, setCurrentApiTransactionId] =
    useState<Nullable<string>>(null);

  const [currentRoutes, setCurrentRoutes] = useState<ApiTransactionRoutes[]>(
    [],
  );

  const [isSuccesCompleted, setIsSuccesCompleted] = useState(false);

  const handleStepperSuccessComplete = useCallback(
    (apiTransactionId: string, routes: ApiTransactionRoutes[]) => {
      setIsSuccesCompleted(true);
      setCurrentApiTransactionId(apiTransactionId);
      setCurrentRoutes(routes);

      value.actions?.onStepperComplete?.(apiTransactionId, routes);
    },
    [value.actions],
  );

  return (
    <AssistantGenerateRoutesDialogByItemsContext.Provider
      value={{
        ...value,
        activeTab: { tab: activeTab, setActiveTab },
        form: {
          selection,
          chargeIndicators,
          isScheduleRouteEnabled: {
            value: isScheduleRouteEnabled,
            setValue: setIsScheduleRouteEnabled,
          },
        },
        stepper: {
          currentApiTransactionId: {
            value: currentApiTransactionId,
            setCurrentApiTransactionId: setCurrentApiTransactionId,
          },
          currentRoutes: {
            value: currentRoutes,
            setCurrentRoutes,
          },
          isSuccesCompleted: {
            value: isSuccesCompleted,
            setIsSuccesCompleted,
          },
        },
        actions: {
          ...value.actions,
          onStepperComplete: handleStepperSuccessComplete,
        },
      }}
    >
      {children}
    </AssistantGenerateRoutesDialogByItemsContext.Provider>
  );
};

export default AssistantGenerateRoutesDialogByItemsProvider;
